import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from '@angular/core';
import { Haptics } from '@freelancer/haptics';
import { Location } from '@freelancer/location';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { BackgroundColor, LogoSize } from '../logo';
import { ViewHeaderBackground, ViewHeaderType } from './view-header.types';

@Component({
  standalone: false,
  selector: 'fl-view-header',
  template: `
    <div
      class="ViewHeaderContainer"
      [attr.data-background]="background"
    >
      <div
        class="ViewHeaderContent"
        [attr.data-side-notch]="sideNotch"
      >
        <div
          class="ViewHeaderContent-inner"
          [class.HasLogo]="logo"
        >
          @if (logo) {
            <fl-logo
              [backgroundColor]="logoColor"
              [size]="LogoSize.SMALL"
            ></fl-logo>
          }
          @if (showBackButton) {
            <div
              class="ViewHeaderContent-action ViewHeaderContent-action--back desktop:hide"
              [class.tablet:hide]="backButtonHideTablet"
              (click)="handleIconClick()"
            >
              @if (type === 'back') {
                <fl-icon
                  i18n-title="Header back button"
                  title="Back"
                  [size]="IconSize.SMALL"
                  [name]="'ui-chevron-left'"
                  [color]="IconColor.INHERIT"
                  [clickAnimation]="'shrink'"
                  [clickable]="true"
                ></fl-icon>
              }
              @if (type !== 'back') {
                <fl-icon
                  i18n-title="Header close button"
                  title="Close"
                  [size]="IconSize.SMALL"
                  [name]="'ui-close'"
                  [color]="IconColor.INHERIT"
                  [clickable]="true"
                ></fl-icon>
              }
            </div>
          }

          @if (!logo) {
            <div
              class="ViewHeaderContent-title"
              [class.ViewHeaderContent-title--indented]="showBackButton"
            >
              <ng-content></ng-content>
              @if (heading) {
                <h1
                  class="text-color-inherit text-mid desktop:text-large desktop_small:text-center"
                >
                  {{ heading }}
                </h1>
              }
            </div>
          }

          @if (showMenu) {
            <fl-button
              class="ViewHeaderContent-action"
              (click)="menuClick.emit()"
            >
              <fl-icon
                i18n-title="Header menu button"
                title="Menu"
                [name]="'ui-show-more'"
                [size]="IconSize.SMALL"
                [color]="IconColor.INHERIT"
              ></fl-icon>
            </fl-button>
          }
        </div>
      </div>

      <ng-content select=".banner"></ng-content>
    </div>
  `,
  styleUrls: ['./view-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewHeaderComponent {
  private haptics = inject(Haptics);
  private location = inject(Location);

  IconSize = IconSize;
  IconColor = IconColor;
  LogoSize = LogoSize;

  @Input() heading?: string;
  @Input() logo = false;

  @Input() logoColor: BackgroundColor = BackgroundColor.DARK;

  @Input() type = ViewHeaderType.BACK;
  @Input() showBackButton = true;
  @Input() showMenu = false;
  @Input() sideNotch = true;
  @Input() customBackAction = false;
  @Input() backButtonHideTablet = true;

  @HostBinding('attr.data-background')
  @Input()
  background = ViewHeaderBackground.DARK;

  @Output() menuClick = new EventEmitter();
  @Output() close = new EventEmitter();
  @Output() customBack = new EventEmitter();

  handleIconClick(): void {
    this.haptics.light();
    if (this.type === ViewHeaderType.BACK) {
      // TODO: T267853 - handle route changes under one heading (eg. pvp tabs)
      if (this.customBackAction) {
        this.customBack.emit();
      } else {
        this.location.back();
      }
    } else {
      this.close.emit();
    }
  }
}
